import React, { useMemo, useState } from 'react';
import * as XLSX from 'xlsx';

// ────────────────────────────────────────────────────────────────────────
// FitReport — readable report for ONE saved run (client item #6).
//
// Replaces the raw-JSON / inputs-only summary on the Dashboard and Run
// History pages. Shows, in this order:
//   1. Results         — overall R², weighted MSE, per-species R²
//   2. Fitted parameters — one table per reaction (name / value)
//   3. Inputs          — model, exponent mode, datasets, optimizer settings
// and offers two downloads built in the browser from the saved run:
//   • Excel (.xlsx)  — SheetJS (`xlsx`, already used by DataFittingPage)
//   • PDF            — opens the print dialog on a clean report page, so
//                      "Save as PDF" needs no extra dependency
//
// `detail` is the object returned by fitRecordStore.fetchFitDetail().
// Final report format still to be confirmed with Madhura (client item #6),
// which is why both formats are offered.
// ────────────────────────────────────────────────────────────────────────

const HIDDEN_KEYS = new Set(['reaction_index', 'rate_units']);

// Rows that are internal / duplicated / very long (Aspen-ready blob, "A internal",
// units, catalyst mass, ...). They stay in the Excel + PDF reports, but on screen
// they sit behind "Show N more details" so the report doesn't scroll for ages.
const DETAIL_KEY = /(^|_)internal$|^aspen|^(ea_unit|a_units|ea_f|w_cat_g|n_mix_mol|scale_factor|fit_mode|exponent_mode)$|^k[fb]_aspen_at_t$/i;
const isDetailRow = (key, text) => key !== 'exponents' && (DETAIL_KEY.test(key) || String(text).length > 60);

const LABELS = {
  Af: 'Forward rate constant (Af)',
  Ab: 'Backward rate constant (Ab)',
  Ea_f_kJ: 'Forward activation energy, Eaf (kJ/mol)',
  Ea_b_kJ: 'Backward activation energy, Eab (kJ/mol)',
  Ea_kJ_mol: 'Activation energy, Ea (kJ/mol)',
  Ea_rev_kJ_mol: 'Backward activation energy, Eab (kJ/mol)',
  Af_aspen: 'Forward rate constant (Af)',
  Ab_aspen: 'Backward rate constant (Ab)',
  exponents: 'Reaction orders',
  overall_r2: 'Overall R\u00B2',
  weighted_mse: 'Weighted MSE',
  per_species_r2: 'R\u00B2 per species',
};

const humanize = (k) => {
  if (LABELS[k]) return LABELS[k];
  const t = String(k).replace(/_/g, ' ').trim();
  return t.charAt(0).toUpperCase() + t.slice(1);
};

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

export const fmtNum = (v) => {
  if (!isNum(v)) return v == null || v === '' ? '\u2014' : String(v);
  const a = Math.abs(v);
  if (a !== 0 && (a < 1e-2 || a >= 1e5)) return v.toExponential(4);
  return v.toFixed(4);
};

const fmtDate = (iso) => {
  if (!iso) return '\u2014';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '\u2014'
    : d.toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const countOf = (v) => {
  if (Array.isArray(v)) return v.length;
  if (v && typeof v === 'object') return Object.keys(v).length;
  return null;
};

// One reaction entry -> [{ name, value }] rows. Handles scalars and a nested
// {species: order} dict; anything else (long arrays, etc.) is left out.
function flattenReaction(entry) {
  if (!entry || typeof entry !== 'object') return [{ name: 'Value', value: fmtNum(entry) }];
  const rows = [];
  Object.entries(entry).forEach(([k, v]) => {
    if (HIDDEN_KEYS.has(k) || v == null) return;
    if (isNum(v) || typeof v === 'string') {
      const value = fmtNum(v);
      rows.push({ name: humanize(k), value, detail: isDetailRow(k, value) });
    } else if (typeof v === 'object' && !Array.isArray(v)) {
      const inner = Object.entries(v).filter(([, x]) => isNum(x) || typeof x === 'string');
      if (inner.length && /^aspen/i.test(k)) {
        // Aspen-ready block: one row per value (was one giant text line), so it
        // flows across the full width as a grid instead of a long paragraph.
        const group = humanize(k).replace(/\s+ready$/i, '');
        inner.forEach(([ik, x]) => rows.push({ name: `${group}: ${humanize(ik)}`, value: fmtNum(x), detail: true, group: 'aspen', key: ik }));
      } else if (inner.length) {
        const value = inner.map(([s, x]) => `${s} = ${fmtNum(x)}`).join(';  ');
        rows.push({ name: humanize(k), value, detail: isDetailRow(k, value) });
      }
    }
  });
  if (entry.rate_units) rows.push({ name: 'Rate constant units', value: String(entry.rate_units) });
  return rows;
}

const reactionTitle = (key, entry, i) => {
  const idx = entry && typeof entry === 'object' && entry.reaction_index != null ? entry.reaction_index : null;
  if (idx != null) return `Reaction ${idx}`;
  const m = /(\d+)$/.exec(String(key));
  return m ? `Reaction ${m[1]}` : `Reaction ${i + 1}`;
};

// ── Reaction equations ─────────────────────────────────────────────────
// The saved run stores each reaction's balanced equation as text
// (fitted_parameters[i].equation, e.g. "HOAc + MeOH -> MeOAc + H2O").
// Show it the way users typed it in the Input section: subscripted formulas and
// a reversible arrow for reversible models:  HOAc + MeOH ⇌ MeOAc + H₂O
const SUB = { 0: '\u2080', 1: '\u2081', 2: '\u2082', 3: '\u2083', 4: '\u2084', 5: '\u2085', 6: '\u2086', 7: '\u2087', 8: '\u2088', 9: '\u2089' };
export function formatEquation(eq, reversible) {
  if (!eq || typeof eq !== 'string') return '';
  const arrow = reversible ? '\u21CC' : '\u2192';
  return eq
    .replace(/\s*(<->|<=>|<-->|->|=>|-->|\u2192|\u21CC|\u21C4|=)\s*/g, ` ${arrow} `)
    .replace(/([A-Za-z)])(\d+)/g, (m, c, d) => c + d.replace(/\d/g, (x) => SUB[x]))
    .replace(/\s+/g, ' ')
    .trim();
}

const equationOf = (entry, reversible) => {
  if (!entry || typeof entry !== 'object') return '';
  return formatEquation(entry.equation || entry.reaction_equation || entry.reaction, reversible);
};

// detail -> ['HOAc + MeOH ⇌ MeOAc + H₂O', ...]  (used by the Reports page group headers)
export function reactionEquationsOf(detail) {
  if (!detail) return [];
  const fp = detail.fitted_parameters;
  const list = Array.isArray(fp) ? fp : Object.values(fp || {});
  const reversible = detail.reversibility !== 'irreversible';
  return list.map((e) => equationOf(e, reversible)).filter(Boolean);
}

// detail -> plain model used by the screen, the Excel sheet and the PDF page
export function buildReportModel(detail) {
  if (!detail) return null;
  const metrics = detail.metrics || {};
  const results = [];
  const overall = metrics.overall_r2 ?? metrics.final_r2;
  if (overall != null) results.push({ name: humanize('overall_r2'), value: fmtNum(overall) });
  if (metrics.weighted_mse != null) results.push({ name: humanize('weighted_mse'), value: fmtNum(metrics.weighted_mse) });
  ['rmse', 'aic', 'bic'].forEach((k) => {
    if (isNum(metrics[k])) results.push({ name: k.toUpperCase(), value: fmtNum(metrics[k]) });
  });

  const perSp = metrics.per_species_r2 ?? metrics.r2_summary ?? metrics.r2_per_species;
  const perSpecies = perSp && typeof perSp === 'object' && !Array.isArray(perSp)
    ? Object.entries(perSp).filter(([, v]) => isNum(v)).map(([sp, v]) => ({ name: sp, value: fmtNum(v) }))
    : [];

  const fp = detail.fitted_parameters;
  const entries = Array.isArray(fp)
    ? fp.map((e, i) => [`reaction_${i + 1}`, e])
    : Object.entries(fp || {});
  const reactions = entries
    .map(([key, entry], i) => ({
      title: reactionTitle(key, entry, i),
      equation: equationOf(entry, detail.reversibility !== 'irreversible'),
      rows: flattenReaction(entry).filter((r) => r.name !== 'Equation'),   // shown in the header instead
    }))
    .filter((r) => r.rows.length || r.equation);

  const cfg = detail.optimizer_config || {};
  const inputs = [
    { name: 'Model', value: detail.model_type === 'lhhw' ? 'LHHW' : 'Power Law' },
    { name: 'Reversibility', value: detail.reversibility === 'irreversible' ? 'Irreversible' : 'Reversible' },
    { name: 'Exponent mode', value: detail.exponent_mode || '\u2014' },
    { name: 'Data sets used', value: countOf(detail.dataset_selection) ?? '\u2014' },
  ];
  if (cfg.n_iter != null) inputs.push({ name: 'Iterations', value: cfg.n_iter });
  if (cfg.r2_target != null) inputs.push({ name: 'R\u00B2 target', value: cfg.r2_target });
  inputs.push({ name: 'Inputs stored with run', value: detail.input_snapshot ? 'Yes' : 'No (saved before this feature)' });

  return {
    title: detail.label || 'Untitled run',
    status: detail.status || '\u2014',
    date: fmtDate(detail.created_at),
    system: detail.system_name || detail.reaction_system_name || '',
    notes: detail.notes || '',
    results, perSpecies, reactions, inputs,
  };
}

// const safeName = (s) => String(s || 'run').replace(/[^\w\-]+/g, '_').slice(0, 60);
const safeName = (s) => String(s || 'run').replace(/[^\w-]+/g, '_').slice(0, 60);

export function downloadReportExcel(model) {
  const wb = XLSX.utils.book_new();

  const summary = [
    ['KinetiQ fit report'],
    ['Run', model.title],
    ['Status', model.status],
    ['Date', model.date],
    ...(model.system ? [['Reaction system', model.system]] : []),
    ...(model.notes ? [['Notes', model.notes]] : []),
    [],
    ['Results'],
    ...(model.results.length ? model.results.map((r) => [r.name, r.value]) : [['No results stored for this run']]),
    [],
    ['Inputs'],
    ...model.inputs.map((r) => [r.name, r.value]),
  ];
  const wsSummary = XLSX.utils.aoa_to_sheet(summary);
  wsSummary['!cols'] = [{ wch: 28 }, { wch: 46 }];
  XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

  const params = [['Reaction', 'Parameter', 'Value']];
  model.reactions.forEach((rx) => {
    if (rx.equation) params.push([rx.title, 'Reaction', rx.equation]);
    rx.rows.forEach((r) => params.push([rx.title, r.name, r.value]));
  });
  if (params.length === 1) params.push(['', 'No fitted parameters stored for this run', '']);
  const wsParams = XLSX.utils.aoa_to_sheet(params);
  wsParams['!cols'] = [{ wch: 14 }, { wch: 42 }, { wch: 40 }];
  XLSX.utils.book_append_sheet(wb, wsParams, 'Fitted parameters');

  if (model.perSpecies.length) {
    const wsSp = XLSX.utils.aoa_to_sheet([['Species', 'R\u00B2'], ...model.perSpecies.map((r) => [r.name, r.value])]);
    wsSp['!cols'] = [{ wch: 20 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, wsSp, 'R2 per species');
  }

  XLSX.writeFile(wb, `${safeName(model.title)}_report.xlsx`);
}

const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

export function downloadReportPdf(model) {
  const table = (rows) => `<table>${rows.map((r) => `<tr><th>${esc(r.name)}</th><td>${esc(r.value)}</td></tr>`).join('')}</table>`;
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(model.title)} - KinetiQ report</title>
<style>
  body{font-family:Segoe UI,Arial,sans-serif;color:#12253D;margin:32px;font-size:13px}
  h1{font-size:20px;margin:0 0 4px} h2{font-size:14px;margin:22px 0 6px;border-bottom:2px solid #00C2A8;padding-bottom:3px}
  h3{font-size:13px;margin:12px 0 4px} .meta{color:#555;margin:0 0 6px} .eq{font-size:14px;font-weight:600;margin:0 0 6px}
  table{border-collapse:collapse;width:100%;margin-bottom:6px} th,td{border:1px solid #ccd5df;padding:5px 8px;text-align:left;vertical-align:top}
  th{background:#f1f5f9;width:42%;font-weight:600}
  @media print{body{margin:14mm}}
</style></head><body>
<h1>${esc(model.title)}</h1>
<p class="meta">KinetiQ fit report &middot; ${esc(model.date)} &middot; Status: ${esc(model.status)}${model.system ? ` &middot; ${esc(model.system)}` : ''}</p>
<h2>Results</h2>${model.results.length ? table(model.results) : '<p>No results stored for this run.</p>'}
${model.perSpecies.length ? `<h3>R&sup2; per species</h3>${table(model.perSpecies)}` : ''}
<h2>Fitted parameters</h2>
${model.reactions.length ? model.reactions.map((rx) => `<h3>${esc(rx.title)}</h3>${rx.equation ? `<p class="eq">${esc(rx.equation)}</p>` : ''}${table(rx.rows)}`).join('') : '<p>No fitted parameters stored for this run.</p>'}
<h2>Inputs</h2>${table(model.inputs)}
${model.notes ? `<h2>Notes</h2><p>${esc(model.notes)}</p>` : ''}
<script>window.onload=function(){setTimeout(function(){window.print();},250);};</script>
</body></html>`;
  const w = window.open('', '_blank');
  if (!w) { window.alert('Please allow pop-ups for this site to download the PDF.'); return; }
  w.document.open();
  w.document.write(html);
  w.document.close();
}

const Section = ({ title, children }) => (
  <div className="fr-section">
    <p className="fr-section-title">{title}</p>
    {children}
  </div>
);

const KeyValueTable = ({ rows }) => (
  <table className="fr-table">
    <tbody>
      {rows.map((r, i) => (
        <tr key={`${r.name}-${i}`}>
          <th scope="row">{r.name}</th>
          <td>{r.value}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

// One reaction.
//  • split (single reaction): key facts on the LEFT, details on the RIGHT, side by side,
//    so the whole panel width is used and nothing needs a toggle.
//  • stacked (several reactions): key facts, with details behind a toggle when
//    there are 3+ cards.
// The Aspen-ready values are shown as one compact "key = value; key = value" block.
// (Excel / PDF keep them as separate rows.)
const ReactionCard = ({ rx, defaultOpen = false, split = false }) => {
  const [showAll, setShowAll] = useState(defaultOpen);
  const main = rx.rows.filter((r) => !r.detail);
  const detailRows = rx.rows.filter((r) => r.detail);
  const hasDetail = detailRows.length > 0;

  const kv = (r, i) => (
    <div key={`${r.name}-${i}`} className={`fr-kv ${String(r.value).length > 60 ? 'fr-kv--wide' : ''}`}>
      <span className="fr-kv-name">{r.name}</span>
      <span className="fr-kv-val">{r.value}</span>
    </div>
  );

  // detail rows in original order, with the Aspen values folded into one block
  const detailNodes = () => {
    const nodes = [];
    let aspenDone = false;
    detailRows.forEach((r, i) => {
      if (r.group === 'aspen') {
        if (aspenDone) return;
        aspenDone = true;
        const text = detailRows.filter((x) => x.group === 'aspen').map((x) => `${x.key} = ${x.value}`).join('; ');
        nodes.push(
          <div key="aspen-block" className="fr-kv fr-kv--wide fr-aspen">
            <span className="fr-kv-name">Aspen ready</span>
            <span className="fr-kv-val">{text}</span>
          </div>
        );
      } else {
        nodes.push(kv(r, i));
      }
    });
    return nodes;
  };

  if (split && hasDetail && main.length > 0) {
    return (
      <div className="fr-rx fr-rx--split">
        <p className="fr-subtitle">{rx.title}</p>
        {rx.equation && <p className="fr-equation" style={{ margin: '2px 0 8px', fontSize: 14, fontWeight: 600, letterSpacing: '0.2px' }}>{rx.equation}</p>}
        <div className="fr-kv-grid fr-kv-grid--one">{main.map(kv)}</div>
        <div className="fr-kv-grid fr-kv-grid--one">{detailNodes()}</div>
      </div>
    );
  }

  const open = showAll || main.length === 0;
  return (
    <div className="fr-rx">
      <p className="fr-subtitle">{rx.title}</p>
      {rx.equation && <p className="fr-equation" style={{ margin: '2px 0 8px', fontSize: 14, fontWeight: 600, letterSpacing: '0.2px' }}>{rx.equation}</p>}
      <div className="fr-kv-grid">
        {main.map(kv)}
        {open && detailNodes()}
      </div>
      {hasDetail && main.length > 0 && (
        <button type="button" className="fr-more" onClick={() => setShowAll((v) => !v)}>
          {showAll ? 'Hide details' : `Show ${detailRows.length} more details`}
        </button>
      )}
    </div>
  );
};

const FitReport = ({ detail, loading, error }) => {
  const model = useMemo(() => buildReportModel(detail), [detail]);

  if (loading) return <p className="dash-summary-text">Loading report&hellip;</p>;
  if (!model) return <p className="dash-summary-error">{error || "Couldn't load this run's report."}</p>;

  return (
    <div className="fr-report">
      <div className="fr-head">
        <div>
          <p className="fr-title">{model.title}</p>
          <p className="fr-sub">{model.date} &middot; {model.status}{model.system ? ` \u00B7 ${model.system}` : ''}</p>
        </div>
        <div className="fr-downloads">
          <button type="button" className="dash-view-all fr-btn" onClick={() => downloadReportExcel(model)}>
            <i className="ti ti-file-spreadsheet" aria-hidden="true" /> Download Excel
          </button>
          <button type="button" className="dash-view-all fr-btn" onClick={() => downloadReportPdf(model)}>
            <i className="ti ti-file-type-pdf" aria-hidden="true" /> Download PDF
          </button>
        </div>
      </div>

      <div className="fr-grid">
        <Section title="Results">
          {model.results.length
            ? <KeyValueTable rows={model.results} />
            : <p className="fr-empty">No results stored for this run{model.status === 'stopped' ? ' (it was stopped before finishing)' : ''}.</p>}
          {model.perSpecies.length > 0 && (
            <>
              <p className="fr-subtitle">R&sup2; per species</p>
              <KeyValueTable rows={model.perSpecies} />
            </>
          )}
        </Section>

        <Section title="Fitted parameters">
          {model.reactions.length
            ? model.reactions.map((rx) => <ReactionCard key={rx.title} rx={rx} defaultOpen={model.reactions.length <= 2} split={model.reactions.length === 1} />)
            : <p className="fr-empty">No fitted parameters stored for this run.</p>}
        </Section>

        <Section title="Inputs">
          <KeyValueTable rows={model.inputs} />
          {model.notes && <p className="fr-notes"><span className="dash-td-label">Notes</span>{model.notes}</p>}
        </Section>
      </div>
    </div>
  );
};

export default FitReport;